import Ellipsis from "@/components/views/Ellipsis";
import { formatLocaleBruttoPoints, formatLocaleCurrency } from "@/utils/formatUtils";
import { isDefined } from "@/utils/utils";
import { Alert, Col, Collapse, Divider, Flex, Row } from "antd";
import { Fragment } from "react";
import { useTranslation } from "react-i18next";
import { formatAgentName } from "../../agent/utils";
import type { PredictedCommission } from "../types";

interface Props {
  predictedCommission?: PredictedCommission;
}

export const ContractPredictedCommissions = ({ predictedCommission }: Props) => {
  const { t } = useTranslation();

  if (!predictedCommission || !predictedCommission.calculatedCommissions.length) {
    return null;
  }

  return (
    <Collapse
      items={[
        {
          key: 1,
          label: predictedCommission?.calculatedCommissions
            .filter((pcc) => pcc.individualBruttoPoints)
            .map((pcc) => (
              <div key={pcc.id}>
                <span
                  style={{
                    width: 250,
                    display: "inline-block",
                    verticalAlign: "top",
                    marginRight: 8,
                  }}
                >
                  <Ellipsis>{formatAgentName(pcc.gainer)}</Ellipsis>
                </span>
                <div style={{ width: 326, display: "inline-block" }}>
                  {pcc.errors.length ? (
                    <Alert
                      type="error"
                      showIcon
                      title={<Ellipsis>{pcc.errors.flatMap((error) => error.description).join(" ")}</Ellipsis>}
                    />
                  ) : (
                    <Flex>
                      <div style={{ flex: 1 }} className="right-align">
                        {isDefined(pcc.commissionAmount) ? formatLocaleCurrency(pcc.commissionAmount) : undefined}
                      </div>
                      <div style={{ flex: 1 }} className="right-align">
                        {isDefined(pcc.bruttoPointAmount) ? formatLocaleBruttoPoints(pcc.bruttoPointAmount) : undefined}
                      </div>
                    </Flex>
                  )}
                </div>
              </div>
            )),
          children: (
            <>
              {predictedCommission?.calculatedCommissions.map((pcc) => (
                <Fragment key={pcc.id}>
                  <Row style={{ marginBottom: 8 }}>
                    <Col span={12} className="left-align">
                      <Ellipsis>{formatAgentName(pcc.gainer)}</Ellipsis>
                    </Col>
                    {pcc.errors.length ? (
                      <Col span={12}>
                        <div style={{ minWidth: 0, maxWidth: 300 }}>
                          <Alert
                            type="error"
                            showIcon
                            title={pcc.errors.flatMap((error) => error.description).join(" ")}
                          />
                        </div>
                      </Col>
                    ) : (
                      <>
                        <Col span={6} className="right-align">
                          {isDefined(pcc.commissionAmount) ? formatLocaleCurrency(pcc.commissionAmount) : undefined}
                        </Col>
                        <Col span={6} className="right-align">
                          {isDefined(pcc.bruttoPointAmount)
                            ? formatLocaleBruttoPoints(pcc.bruttoPointAmount)
                            : undefined}
                        </Col>
                      </>
                    )}
                  </Row>
                  {pcc.individualBruttoPoints && <Divider style={{ margin: "16px 0" }} />}
                </Fragment>
              ))}

              <Row>
                {isDefined(predictedCommission?.commissionBaseAmount) && (
                  <Col span={12}>
                    <b>{`${t("contract.attrs.predictedCommissions.commissionBaseAmount")}: ${formatLocaleCurrency(predictedCommission.commissionBaseAmount)}`}</b>
                  </Col>
                )}
                {isDefined(predictedCommission?.commissionAmount) && (
                  <Col span={6} className="right-align">
                    <b>{`${t("contract.attrs.predictedCommissions.commissionAmount")}: ${formatLocaleCurrency(predictedCommission?.commissionAmount)}`}</b>
                  </Col>
                )}
              </Row>

              {!!predictedCommission?.errors.length && (
                <Row>
                  <Col span={24} className="margin-top-tiny">
                    <Alert
                      type="error"
                      showIcon
                      title={predictedCommission.errors.flatMap((e) => e.description).join(" ")}
                    />
                  </Col>
                </Row>
              )}
            </>
          ),
        },
      ]}
    />
  );
};
